import { Effect, Match, ParseResult, Schema } from "effect";
// Exhcnage Rate
const CurrencyExchangeRateString = Schema.String.pipe(Schema.annotations({
    title: "Currency Exchange Rate",
    description: "The exchange rate for a currency pair",
    examples: [
        "1.23",
        "1.00",
        "0.98"
    ]
}), Schema.brand("@open-revenue/schema/Currency/ExchangeRate"));
const CurrencyExchangeRateFloat = Schema.Number.pipe(Schema.annotations({
    title: "Currency Exchange Rate",
    description: "The exchange rate for a currency pair",
    examples: [
        1.23,
        1.0,
        0.98
    ]
}), Schema.brand("@open-revenue/schema/Currency/ExchangeRateFloat"));
const CurrencyExchangeRateStringFromFloatSchema = Schema.transform(CurrencyExchangeRateFloat, CurrencyExchangeRateString, {
    strict: true,
    decode: (exchangeRate)=>{
        return exchangeRate.toString();
    },
    encode: (exchangeRate)=>{
        return parseFloat(exchangeRate);
    }
});
const CurrencyExchangeRateFloatFromStringSchema = Schema.transform(CurrencyExchangeRateString, CurrencyExchangeRateFloat, {
    strict: true,
    decode: (exchangeRate)=>{
        return parseFloat(exchangeRate);
    },
    encode: (exchangeRate)=>{
        return exchangeRate.toString();
    }
});
export const CurrencyExchangeRate = {
    String: {
        Schema: CurrencyExchangeRateString,
        Type: typeof CurrencyExchangeRateString.Type,
        FromFloatSchema: CurrencyExchangeRateStringFromFloatSchema,
        toFloat: (exchangeRate)=>parseFloat(exchangeRate),
        fromFloat: (exchangeRate)=>exchangeRate.toString(),
        makeUnsafe: (exchangeRate)=>exchangeRate,
        make: (exchangeRate)=>Schema.decodeUnknown(CurrencyExchangeRateString)(exchangeRate),
        fake: ()=>(Math.random() * 2).toFixed(2)
    },
    Float: {
        Schema: CurrencyExchangeRateFloat,
        Type: typeof CurrencyExchangeRateFloat.Type,
        FromStringSchema: CurrencyExchangeRateFloatFromStringSchema,
        toString: (exchangeRate)=>exchangeRate.toString(),
        fromString: (exchangeRate)=>parseFloat(exchangeRate),
        makeUnsafe: (exchangeRate)=>exchangeRate,
        make: (exchangeRate)=>Schema.decodeUnknown(CurrencyExchangeRateFloat)(exchangeRate),
        fake: ()=>Math.random() * 2
    }
};
// Currency ISO 4217
const CurrencyISO4217Examples = [
    "EUR",
    "USD"
];
export const CurrencyISO4217Schema = Schema.String.annotations({
    title: "Currency ISO",
    description: "ISO 4217 currency code",
    examples: CurrencyISO4217Examples
}).pipe(Schema.pattern(/^[A-Z]{3}$/), Schema.brand("@open-revenue/schema/Currency/ISO4217"));
const CurrencySymbolExamples = [
    "€",
    "$"
];
const CurrencySymbolSchema = Schema.String.annotations({
    title: "Currency Symbol",
    description: "Currency symbol",
    examples: CurrencySymbolExamples
}).pipe(Schema.brand("@open-revenue/schema/Currency/Symbol"));
const UppercasedFromString = Schema.transform(Schema.String, Schema.String, {
    encode: (value)=>value.toUpperCase(),
    decode: (value)=>value.toUpperCase(),
    strict: true
});
const CurrencyISO4217SchemaFromLowercase = Schema.compose(UppercasedFromString, CurrencyISO4217Schema);
const CurrencyCode = {
    ISO4217: {
        FromLowercase: CurrencyISO4217SchemaFromLowercase,
        Schema: CurrencyISO4217Schema,
        USD: "USD",
        EUR: "EUR",
        CAD: "CAD",
        GBP: "GBP",
        Type: CurrencyISO4217Schema.Type,
        makeUnsafe: (currency)=>currency,
        make: (currency)=>Schema.decodeUnknown(CurrencyISO4217Schema)(currency),
        fake: ()=>CurrencyISO4217Examples[Math.floor(Math.random() * CurrencyISO4217Examples.length)]
    },
    Symbol: {
        Schema: CurrencySymbolSchema,
        Type: CurrencySymbolSchema.Type,
        fake: ()=>CurrencySymbolExamples[Math.floor(Math.random() * CurrencySymbolExamples.length)],
        makeUnsafe: (currency)=>currency,
        make: (currency)=>Schema.decodeUnknown(CurrencySymbolSchema)(currency)
    }
};
// Currency Amount Millis
const CurrencyAmountMillisStringSchema = Schema.String.annotations({
    title: "Currency Amount Millis",
    description: "Currency amount in millicents. 1000 millicents = 1 cent. Represented as a string",
    examples: [
        "1993"
    ]
}).pipe(Schema.pattern(/^\d+$/), Schema.brand("@open-revenue/schema/Currency/AmountMillis"));
const CurrencyAmountMillisFloatSchema = Schema.Number.annotations({
    title: "Currency Amount Millis Float",
    description: "Currency amount in millicents. 1000 millicents = 1 cent. Represented as a float",
    examples: [
        1000.39,
        1993.99
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountMillisFloat"));
// Currency Amount Millis Int
const CurrencyAmountMillisIntSchema = Schema.Number.annotations({
    title: "Currency Amount Millis Int",
    description: "Currency amount in millicents. 1000 millicents = 1 cent. Represented as an integer",
    examples: [
        1000,
        199,
        9999
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountMillisInt"));
const CurrencyAmountMillisIntFromStringSchema = Schema.transform(CurrencyAmountMillisStringSchema, CurrencyAmountMillisIntSchema, {
    strict: true,
    decode: (amount)=>{
        return parseInt(amount);
    },
    encode: (amount)=>{
        return amount.toString();
    }
});
const CurrencyAmountMillisIntFromFloatStringSchema = Schema.transform(Schema.String, CurrencyAmountMillisIntSchema, {
    strict: true,
    decode: (amount)=>{
        return Math.round(parseFloat(amount) * 1000);
    },
    encode: (amount)=>{
        return (amount / 1000).toString();
    }
});
const CurrencyAmountMillisStringFromIntSchema = Schema.transform(CurrencyAmountMillisIntSchema, CurrencyAmountMillisStringSchema, {
    strict: true,
    decode: (amount)=>{
        return amount.toString();
    },
    encode: (amount)=>{
        return parseInt(amount);
    }
});
const CurrencyAmountMillisInt = {
    Schema: CurrencyAmountMillisIntSchema,
    FromStringSchema: CurrencyAmountMillisIntFromStringSchema,
    FromFloatStringSchema: CurrencyAmountMillisIntFromFloatStringSchema,
    fake: ()=>Math.floor(Math.random() * 1000000),
    makeUnsafe: (amount)=>amount,
    toCurrencyAmountFloat: (amount)=>amount / 1000.0,
    fromString: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisIntFromStringSchema)(amount),
    fromFloatString: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisIntFromFloatStringSchema)(amount),
    toString: (amount)=>amount.toString(),
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisIntSchema)(amount)
};
const CurrencyAmountMillisFloat = {
    Schema: CurrencyAmountMillisFloatSchema,
    // FromStringSchema: CurrencyAmountMillisFloatFromStringSchema,
    fake: ()=>Math.random() * 1000000,
    makeUnsafe: (amount)=>amount,
    // fromString: (amount: string) => Schema.decodeUnknown(CurrencyAmountMillisFloatFromStringSchema)(amount),
    fromInt: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisFloatSchema)(amount),
    toCurrencyAmountFloat: (amount)=>amount / 1000.0
};
const CurrencyAmountMillisString = {
    Schema: CurrencyAmountMillisStringSchema,
    FromIntSchema: CurrencyAmountMillisStringFromIntSchema,
    fake: ()=>Math.floor(Math.random() * 1000000).toString(),
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisStringSchema)(amount),
    fromInt: (amount)=>Schema.decodeUnknown(CurrencyAmountMillisStringSchema)(amount)
};
const CurrencyAmountMillis = {
    Int: CurrencyAmountMillisInt,
    String: CurrencyAmountMillisString,
    Float: CurrencyAmountMillisFloat
};
// Currency Amount Centis
const CurrencyAmountCentisStringSchema = Schema.String.annotations({
    title: "Currency Amount Centis",
    description: "Currency amount in cents. 100 cents = 1 dollar. Represented as a string",
    examples: [
        "199"
    ]
}).pipe(Schema.pattern(/^\d+$/), Schema.brand("@open-revenue/schema/Currency/AmountCentis"));
const CurrencyAmountCentisFloatSchema = Schema.Number.annotations({
    title: "Currency Amount Centis Float",
    description: "Currency amount in cents. 100 cents = 1 dollar. Represented as a float",
    examples: [
        100.39,
        199.99
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountCentisFloat"));
const CurrencyAmountCentisIntSchema = Schema.Number.annotations({
    title: "Currency Amount Centis Int",
    description: "Currency amount in cents. 100 cents = 1 dollar. Represented as an integer",
    examples: [
        100,
        199,
        999
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountCentisInt"));
const CurrencyAmountCentisIntFromStringSchema = Schema.transform(CurrencyAmountCentisStringSchema, CurrencyAmountCentisIntSchema, {
    strict: true,
    decode: (amount)=>{
        return parseInt(amount);
    },
    encode: (amount)=>{
        return amount.toString();
    }
});
const CurrencyAmountCentisIntFromFloatStringSchema = Schema.transform(Schema.String, CurrencyAmountCentisIntSchema, {
    strict: true,
    decode: (amount)=>{
        return Math.round(parseFloat(amount) * 100);
    },
    encode: (amount)=>{
        return (amount / 100).toString();
    }
});
const CurrencyAmountCentisStringFromIntSchema = Schema.transform(CurrencyAmountCentisIntSchema, CurrencyAmountCentisStringSchema, {
    strict: true,
    decode: (amount)=>{
        return amount.toString();
    },
    encode: (amount)=>{
        return parseInt(amount);
    }
});
const CurrencyAmountCentisInt = {
    Schema: CurrencyAmountCentisIntSchema,
    FromStringSchema: CurrencyAmountCentisIntFromStringSchema,
    FromFloatStringSchema: CurrencyAmountCentisIntFromFloatStringSchema,
    fake: ()=>Math.floor(Math.random() * 10000),
    makeUnsafe: (amount)=>amount,
    toCurrencyAmountFloat: (amount)=>amount / 100.0,
    fromString: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisIntFromStringSchema)(amount),
    fromFloatString: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisIntFromFloatStringSchema)(amount),
    toString: (amount)=>amount.toString(),
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisIntSchema)(amount)
};
const CurrencyAmountCentisFloat = {
    Schema: CurrencyAmountCentisFloatSchema,
    fake: ()=>Math.random() * 10000,
    makeUnsafe: (amount)=>amount,
    fromInt: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisFloatSchema)(amount),
    toCurrencyAmountFloat: (amount)=>amount / 100.0
};
const CurrencyAmountCentisString = {
    Schema: CurrencyAmountCentisStringSchema,
    FromIntSchema: CurrencyAmountCentisStringFromIntSchema,
    fake: ()=>Math.floor(Math.random() * 10000).toString(),
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisStringSchema)(amount),
    fromInt: (amount)=>Schema.decodeUnknown(CurrencyAmountCentisStringSchema)(amount)
};
const CurrencyAmountCentis = {
    Int: CurrencyAmountCentisInt,
    String: CurrencyAmountCentisString,
    Float: CurrencyAmountCentisFloat
};
// Currency Floats
const CurrencyAmountStringSchema = Schema.String.annotations({
    title: "Currency Amount String",
    description: "Currency amount in seconds",
    examples: [
        "1.99",
        "99.99"
    ]
}).pipe(Schema.pattern(/^\d+(\.\d+)?$/), Schema.brand("@open-revenue/schema/Currency/AmountString"));
// Float
const CurrencyAmountFloatSchema = Schema.Number.annotations({
    title: "Currency Amount Float",
    description: "Currency amount in seconds",
    examples: [
        1.99,
        99.99
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountFloat"));
const CurrencyAmountFloatFromStringSchema = Schema.transform(CurrencyAmountStringSchema, CurrencyAmountFloatSchema, {
    strict: true,
    decode: (amount)=>{
        return parseFloat(amount);
    },
    encode: (amount)=>{
        return amount.toString();
    }
});
const CurrencyAmountStringFromFloatSchema = Schema.transform(CurrencyAmountFloatSchema, CurrencyAmountStringSchema, {
    strict: true,
    decode: (amount)=>{
        return amount.toString();
    },
    encode: (amount)=>{
        return parseFloat(amount);
    }
});
// Currency Amount Currency Coded String
const CurrencyAmountCurrencyCodedStringSchema = Schema.String.annotations({
    title: "Currency Amount Currency Coded String",
    description: "Currency amount in seconds",
    examples: [
        "$1.99",
        "$99.99"
    ]
}).pipe(Schema.brand("@open-revenue/schema/Currency/AmountCurrencyCodedString"));
const CurrencyAmountCurrencyCodedString = {
    Schema: CurrencyAmountCurrencyCodedStringSchema,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountCurrencyCodedStringSchema)(amount)
};
const CurrencyAmountString = {
    Schema: CurrencyAmountStringSchema,
    FromFloatSchema: CurrencyAmountFloatFromStringSchema,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountStringSchema)(amount),
    fromFloat: (amount)=>Schema.encode(CurrencyAmountFloatFromStringSchema)(amount),
    fromString: (amount)=>Schema.decodeUnknown(CurrencyAmountStringFromFloatSchema)(amount)
};
const CurrencyAmountFloat = {
    Schema: CurrencyAmountFloatSchema,
    FromStringSchema: CurrencyAmountStringFromFloatSchema,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountFloatSchema)(amount),
    fromString: (amount)=>Schema.decodeUnknown(CurrencyAmountStringFromFloatSchema)(amount),
    toString: (amount)=>Schema.encode(CurrencyAmountFloatFromStringSchema)(amount)
};
const CurrencyAmountUnitAmountSchema = Schema.Union(Schema.Literal("millis"), Schema.Literal("centis"), Schema.Literal("zero")).pipe(Schema.brand("@open-revenue/schema/Currency/AmountUnitAmount"));
const CurrencyAmountUnitAmountDivisorFromUnitAmountSchema = Schema.transformOrFail(CurrencyAmountUnitAmountSchema, Schema.Number, {
    decode: (unitAmount)=>Effect.gen(function*() {
            return Match.value(unitAmount).pipe(Match.when("millis", ()=>1000), Match.when("centis", ()=>100), Match.when("zero", ()=>1), Match.exhaustive);
        }),
    encode: (_, __, ast)=>Effect.fail(new ParseResult.Forbidden(ast, "", "Not allowed to encode")),
    strict: true
});
const CurrencyAmountUnitAmount = {
    Schema: CurrencyAmountUnitAmountSchema,
    Millis: "millis",
    Centis: "centis",
    Zero: "zero",
    Type: CurrencyAmountUnitAmountSchema.Type,
    DivisorFromUnitAmountSchema: CurrencyAmountUnitAmountDivisorFromUnitAmountSchema,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountUnitAmountSchema)(amount)
};
const CurrencyAmountAmountNumberSchema = Schema.Number.pipe(Schema.brand("@open-revenue/schema/Currency/Amount/String"));
const CurrencyAmountAmount = {
    Schema: CurrencyAmountAmountNumberSchema,
    FromString: Schema.compose(Schema.NumberFromString, CurrencyAmountAmountNumberSchema),
    Type: CurrencyAmountAmountNumberSchema.Type,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountAmountNumberSchema)(amount)
};
const CurrencyAmountAmountStringSchema = Schema.String.pipe(Schema.brand("@open-revenue/schema/Currency/Amount/String"));
const CurrencyAmountAmountString = {
    Schema: CurrencyAmountAmountStringSchema,
    Type: CurrencyAmountAmountStringSchema.Type,
    makeUnsafe: (amount)=>amount,
    make: (amount)=>Schema.decodeUnknown(CurrencyAmountAmountStringSchema)(amount)
};
export const CurrencyAmount = {
    // The actual amount representations as number or string
    AmountNumber: CurrencyAmountAmount,
    AmountString: CurrencyAmountAmountString,
    //
    Float: CurrencyAmountFloat,
    String: CurrencyAmountString,
    Millis: CurrencyAmountMillis,
    Centis: CurrencyAmountCentis,
    CurrencyCodedString: CurrencyAmountCurrencyCodedString,
    UnitAmount: CurrencyAmountUnitAmount
};
export const Currency = {
    Code: CurrencyCode,
    Amount: CurrencyAmount,
    ExchangeRate: CurrencyExchangeRate
};
